import * as React from "react";
import {
  Box,
  Fab,
  Zoom,
  useMediaQuery,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import CloseIcon from "@mui/icons-material/Close";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/X";
import BookIcon from "@mui/icons-material/Book";

export default function FloatingSocialButtons() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = React.useState(false);
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  // Show scroll-to-top after scrolling down
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleToggle = () => {
    if (isMobile) {
      setOpen((prev) => !prev);
    }
  };

  const handleClickAway = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const socialButtons = [
    {
      icon: <InstagramIcon />,
      link: "https://www.instagram.com/sharkshooter2011/",
      label: "Instagram",
    },
    { icon: <TwitterIcon />, link: "https://x.com/neki2011", label: "Twitter" },
    {
      icon: <YouTubeIcon />,
      link: "https://www.youtube.com/@sharkshooter2011",
      label: "YouTube",
    },
    {
      icon: <FacebookIcon />,
      link: "https://www.facebook.com/muneki.samejima",
      label: "Facebook",
    },
    {
      icon: <BookIcon />,
      link: "https://note.com/sharkshooter2011",
      label: "Note",
    },
  ];

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 32, md: "35%" },
          right: 32,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          zIndex: 1300,

          "& .MuiFab-root": {
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            boxShadow: 4,
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: 8,
            },
          },
        }}
      >
        {/* ===== DESKTOP MODE ===== */}
        {!isMobile && (
          <>
            {socialButtons.map((btn, index) => (
              <Zoom in key={index}>
                <Tooltip title={btn.label} placement="left" arrow>
                  <Fab
                    size="medium"
                    color="primary"
                    onClick={() => window.open(btn.link, "_blank")}
                  >
                    {btn.icon}
                  </Fab>
                </Tooltip>
              </Zoom>
            ))}

            {showScrollTop && (
              <Zoom in>
                <Tooltip title="Back to Top" placement="left" arrow>
                  <Fab size="medium" color="secondary" onClick={scrollToTop}>
                    <KeyboardArrowUpIcon />
                  </Fab>
                </Tooltip>
              </Zoom>
            )}
          </>
        )}

        {/* ===== MOBILE MODE ===== */}
        {isMobile && (
          <>
            {/* Social Stack (only social buttons) */}
            {open &&
              socialButtons.map((btn, index) => (
                <Zoom in key={index}>
                  <Fab
                    size="medium"
                    color="primary"
                    onClick={() => window.open(btn.link, "_blank")}
                  >
                    {btn.icon}
                  </Fab>
                </Zoom>
              ))}

            {/* Scroll To Top — independent button */}
            {showScrollTop && (
              <Zoom in={showScrollTop}>
                <Fab size="medium" color="secondary" onClick={scrollToTop}>
                  <KeyboardArrowUpIcon />
                </Fab>
              </Zoom>
            )}

            {/* Share Toggle */}
            <Fab size="medium" color="default" onClick={handleToggle}>
              {open ? <CloseIcon /> : <RssFeedIcon />}
            </Fab>
          </>
        )}
      </Box>
    </ClickAwayListener>
  );
}
